<template>
  <div class="row">
    <div class="col-12 mb-4">
      <div class="card" style="border-radius: 10px">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0" style="font-weight: bold">{{ $t("styleAnalysesChart.title") }}</h5>
            <div class="d-flex align-items-center ms-auto">
              <button @click="navigateTimePeriod(-1)" class="btn btn-sm btn-outline-secondary me-2" :disabled="loading">
                <font-awesome-icon icon="chevron-left" />
              </button>
              <button
                @click="navigateTimePeriod(1)"
                class="btn btn-sm btn-outline-secondary"
                :disabled="loading || isCurrentPeriod"
              >
                <font-awesome-icon icon="chevron-right" />
              </button>
            </div>
          </div>
          <div class="period-label mb-2" style="font-size: 12px">
            <span class="text-muted">{{ currentPeriodLabel }}</span>
          </div>

          <div class="chart-container">
            <chart
              v-if="!loading"
              :key="chartKey"
              :series="series"
              :category-axis="categoryAxis"
              :value-axis="valueAxis"
              :tooltip="tooltipOptions"
              :legend="legendOptions"
              :theme="'sass'"
              :chart-area="{ height: 300 }"
            >
            </chart>
            <div v-else class="d-flex justify-content-center align-items-center h-100">
              <GridLoading></GridLoading>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { DashboardApi } from "@/api";
import { KendoChart } from "@progress/kendo-charts-vue-wrapper";
import { defineComponent } from "vue";
import GridLoading from "./GridLoading.vue";
import { useToast } from "vue-toastification";

export default defineComponent({
  name: "StyleAnalysesChart",
  components: { chart: KendoChart, GridLoading },
  setup() {
    const dashboardApi = new DashboardApi()!;

    return {
      dashboardApi
    };
  },
  props: {
    chartData: {
      type: Array as () => any[],
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      currentOffset: 0,
      chartKey: 0,
      resizeTimeout: null as any,
      toast: null as any,
      series: [
        {
          type: "area",
          style: "spline",
          data: [],
          name: "",
          color: "#72b8ffac",
          opacity: 0.3,
          tooltip: {
            visible: false
          },
          markers: {
            visible: false
          },
          line: {
            style: "smooth",
            width: 0
          }
        },
        {
          type: "line",
          style: "smooth",
          data: [],
          name: "",
          color: "#0054a6",
          highlight: {
            visible: true
          },
          markers: {
            visible: true,
            size: 7,
            background: "#0054a6",
            border: {
              color: "#0054a6",
              width: 1
            }
          },
          line: {
            color: "#0054a6",
            width: 2
          }
        }
      ],
      categoryAxis: {
        categories: [],
        justified: true,
        labels: {
          rotation: 0,
          font: "12px Arial"
        },
        majorGridLines: {
          visible: false
        },
        line: {
          visible: true
        }
      },
      valueAxis: {
        type: "numeric",
        labels: {
          format: "{0}",
          font: "12px Arial"
        },
        title: {
          text: "",
          font: "12px Arial"
        },
        min: 0,
        majorGridLines: {
          visible: true,
          color: "#f0f0f0"
        }
      },
      tooltipOptions: {
        visible: true,
        template: (e: any) => {
          const match = /^(.*) (\d{4})$/.exec(e.category);
          const month = match ? match[1] : e.category;
          const year = match ? match[2] : "";
          return `<div class="custom-tooltip">
                    <span style="font-size: 12px;">
                      <b><span>${month} ${year}</span></b><br/>
                      <span>${e.value} ${this.$t("styleAnalysesChart.toolTipLabel")}</span>
                    </span>
                  </div>`;
        },
        background: "#0054a6"
      },
      legendOptions: {
        visible: false
      }
    };
  },
  computed: {
    currentPeriodLabel(): string {
      const now = new Date();
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1 + this.currentOffset, 0);
      const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 11, 1);
      const startMonth = this.getMonthName(startDate.getMonth());
      const endMonth = this.getMonthName(endDate.getMonth());
      return `${startMonth} ${startDate.getFullYear()} - ${endMonth} ${endDate.getFullYear()}`;
    },
    isCurrentPeriod(): boolean {
      return this.currentOffset === 0;
    }
  },
  methods: {
    getMonthName(monthIndex: number): string {
      const monthKeys = [
        "months.january",
        "months.february",
        "months.march",
        "months.april",
        "months.may",
        "months.june",
        "months.july",
        "months.august",
        "months.september",
        "months.october",
        "months.november",
        "months.december"
      ];
      return this.$t(monthKeys[monthIndex]);
    },

    navigateTimePeriod(direction: number): void {
      this.currentOffset += direction * 12;
      this.loadChartData();
    },

    generateMonthLabels(): string[] {
      const labels = [];
      const now = new Date();
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1 + this.currentOffset, 0);

      for (let i = 11; i >= 0; i--) {
        const date = new Date(endDate.getFullYear(), endDate.getMonth() - i, 1);
        const monthName = this.getMonthName(date.getMonth());
        labels.push(`${monthName} ${date.getFullYear()}`);
      }
      return labels;
    },
    generateMonthKeys(): string[] {
      const keys: string[] = [];
      const now = new Date();
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1 + this.currentOffset, 0);

      for (let i = 11; i >= 0; i--) {
        const date = new Date(endDate.getFullYear(), endDate.getMonth() - i, 1);
        keys.push(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`);
      }
      return keys;
    },
    getChartData(): Promise<number[]> {
      return this.dashboardApi.apiGetStyleAnalysesCountByMonthGet(this.currentOffset).then((response: any) => {
        const apiData = response.data;
        return this.generateMonthKeys().map((key: string) => apiData[key] || 0);
      });
    },
    async loadChartData(): Promise<void> {
      this.loading = true;

      try {
        const data: any = await this.getChartData();
        const categories: any = this.generateMonthLabels();

        this.series[0].data = data;
        this.series[1].data = data;
        this.categoryAxis.categories = categories;

        this.chartKey++;

        this.series[1].name = this.$t("styleAnalysesChart.title");
        this.valueAxis.title.text = this.$t("styleAnalysesChart.yAxisLabel");
      } catch (error) {
        this.toast.error(this.$t("styleAnalysesChart.loadError") + ": " + error, {
          toastClassName: "custom-error-toast",
          timeout: 5000
        });
      } finally {
        this.loading = false;
      }
    },

    handleResize(): void {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.chartKey++;
      }, 100);
    }
  },

  created(): void {
    this.toast = useToast();
    this.series[1].name = this.$t("styleAnalysesChart.title");
    this.valueAxis.title.text = this.$t("styleAnalysesChart.yAxisLabel");
    this.loadChartData();
  },

  mounted(): void {
    window.addEventListener("resize", this.handleResize);
  },

  beforeUnmount(): void {
    window.removeEventListener("resize", this.handleResize);
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }
});
</script>
<style scoped>
.period-label {
  text-align: left;
  font-size: 1rem;
  font-weight: 500;
}
.chart-container {
  width: 100% !important;
  height: 100% !important;
  overflow: hidden;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  position: relative;
}
.card-body {
  overflow: hidden;
  box-sizing: border-box;
}
.chart-container >>> .k-series-1 .k-marker:hover {
  fill: #ffffff !important;
  stroke: #70b2f5ac !important;
  stroke-width: 2px !important;
}
.custom-tooltip {
  font-size: 12px !important;
  line-height: 1.3;
}
.custom-tooltip b {
  font-weight: bold;
  font-size: 13px;
}
</style>
